import {
  bottomLevel,
  bottomLevelId,
  groupByWorldKey,
  worldKeyForBuildingEntity,
} from "building-generation/elements/level/levelTools";
import type { Building } from "building-types/building/buildingTypes";
import type {
  GeneratedFramedFloorAssembly,
  GeneratedSlabFloorAssembly,
} from "building-types/horizontalAssemblies/generatedFloorAssemblyTypes";
import {
  BuildingGenerationIssueType,
  type BuildingGenerationIssue,
} from "building-types/settings/buildingGenerationIssue";
import type { RWTable } from "common/data/table";
import { getFloorSplitData } from "doc/elements/floor/floorAssembly";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import {
  collectBoundariesToProcess,
  createFramedFloorAssembly,
  createSlabFloorAssembly,
  extractFrameFloorAssemblyProperties,
  extractSlabFloorAssemblyProperties,
  processFloorBoundariesToGeometry,
  shouldProcessFloorWithBoundaries,
} from "../generationTools/generateFloorHorizontalAssembliesTools";
import { calcLevelFloorSettings } from "../generationTools/generateLevelsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  doc: {
    optionSets,
    instanceOptionSets,
    settings: settingsTable,
    levels: levelDefs,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
    componentInstances,
  },
  horizontalAssemblyFloorSystems: floorSystems,
  horizontalAssemblyFloors: floors,
  levels,
  resolvedDatums,
  optionChoices,
  settings,
  entityDisabledByGroup,
  mirror,
  mirrorTranslationX,
  floorBoundaries,
  idToParents,
}: Pick<
  Building,
  | "doc"
  | "horizontalAssemblyFloorSystems"
  | "horizontalAssemblyFloors"
  | "levels"
  | "resolvedDatums"
  | "optionChoices"
  | "settings"
  | "entityDisabledByGroup"
  | "mirror"
  | "mirrorTranslationX"
  | "floorBoundaries"
  | "idToParents"
>) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula({ floors, floorSystems }, definitions, optionChoices);
  const levelsFloorSettings = calcLevelFloorSettings(settingsTable, levelDefs, settings);

  return {
    floorSystems,
    floors,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
    levels,
    levelsFloorSettings,
    resolvedDatums,
    resolvedOptionChoices,
    entityDisabledByGroup,
    mirror,
    mirrorTranslationX,
    floorBoundaries,
    idToParents,
    componentInstances,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  resolvedOptionChoices: "shallow",
  levelsFloorSettings: "deep",
  idToParents: "shallow",
  componentInstances: "shallow",
  floors: "shallow",
  floorSystems: "shallow",
});

function generateFloorHorizontalAssembliesForWorlds(
  input: ReturnType<typeof select>,
): Pick<Building, "framedFloorAssemblies" | "slabFloorAssemblies" | "issues"> {
  const { levels, floors, floorSystems, floorBoundaries, idToParents, componentInstances } = input;
  const levelsByWorldKey = groupByWorldKey(levels, (id) =>
    worldKeyForBuildingEntity(id, idToParents, componentInstances),
  );
  const floorsByWorldKey = groupByWorldKey(floors, (_, floor) =>
    worldKeyForBuildingEntity(floor.parentId, idToParents, componentInstances),
  );
  const floorSystemsByWorldKey = groupByWorldKey(floorSystems, (id) =>
    worldKeyForBuildingEntity(id, idToParents, componentInstances),
  );
  const floorBoundariesByWorldKey = groupByWorldKey(floorBoundaries, (id, boundary) => {
    const parentFloorId = boundary.parentFloorId ?? id;
    const memberEntityId = floors[parentFloorId]?.parentId ?? parentFloorId;
    return worldKeyForBuildingEntity(memberEntityId, idToParents, componentInstances);
  });

  let framedFloorAssemblies: RWTable<GeneratedFramedFloorAssembly> = {};
  let slabFloorAssemblies: RWTable<GeneratedSlabFloorAssembly> = {};
  let issues: BuildingGenerationIssue[] = [];

  for (const worldKey in levelsByWorldKey) {
    const worldResult = generate({
      ...input,
      levels: levelsByWorldKey[worldKey] ?? {},
      floors: floorsByWorldKey[worldKey] ?? {},
      floorSystems: floorSystemsByWorldKey[worldKey] ?? {},
      floorBoundaries: floorBoundariesByWorldKey[worldKey] ?? {},
    });
    framedFloorAssemblies = { ...framedFloorAssemblies, ...worldResult.framedFloorAssemblies };
    slabFloorAssemblies = { ...slabFloorAssemblies, ...worldResult.slabFloorAssemblies };
    issues = [...issues, ...worldResult.issues];
  }

  return { framedFloorAssemblies, slabFloorAssemblies, issues };
}

function generate({
  floorSystems,
  floors,
  frameFloorAssemblies,
  slabFloorAssemblies,
  framingComponents,
  slabComponents,
  sheathingComponents,
  resolvedDatums,
  levels,
  levelsFloorSettings,
  resolvedOptionChoices,
  entityDisabledByGroup,
  mirror,
  mirrorTranslationX,
  floorBoundaries,
}: ReturnType<typeof select>): Pick<Building, "framedFloorAssemblies" | "slabFloorAssemblies" | "issues"> {
  const issues: BuildingGenerationIssue[] = [];
  const generatedframedFloorAssemblies: RWTable<GeneratedFramedFloorAssembly> = {};
  const generatedSlabFloorAssemblies: RWTable<GeneratedSlabFloorAssembly> = {};

  if (!floorSystems || !floors) {
    return {
      framedFloorAssemblies: generatedframedFloorAssemblies,
      slabFloorAssemblies: generatedSlabFloorAssemblies,
      issues,
    };
  }

  const resolvedFloorSystems = evalFormulas(
    floorSystems,
    (v) => resolvedOptionChoices[v],
    undefined,
    entityDisabledByGroup,
  );
  const resolvedFloors = evalFormulas(floors, (v) => resolvedOptionChoices[v], undefined, entityDisabledByGroup);

  for (const floorId in resolvedFloors) {
    const floor = resolvedFloors[floorId];
    const floorSystemId = floor.parentId;
    const parentFloorSystem = resolvedFloorSystems?.[floorSystemId];

    if (!shouldProcessFloorWithBoundaries(floor, floorSystemId, resolvedFloorSystems, floorBoundaries)) {
      continue;
    }

    const boundaries = floorBoundaries[floorId];
    if (!boundaries?.perimeters.length) {
      continue;
    }

    const effectiveFloor = {
      ...floor,
      assemblyId: parentFloorSystem.assemblyId,
      spanDirection: parentFloorSystem.spanDirection,
      framingAngle: parentFloorSystem.framingAngle,
      framingStartPt: parentFloorSystem.framingStartPt,
      lateralOffset: parentFloorSystem.lateralOffset,
      topDatum: parentFloorSystem.topDatum,
      openingFramingConfig: parentFloorSystem.openingFramingConfig,
    };

    const splitData = getFloorSplitData(floor);

    const firstLevel = bottomLevel(levels);
    if (!firstLevel) {
      continue;
    }

    const boundariesToProcess = collectBoundariesToProcess(
      floorId,
      boundaries,
      floorBoundaries,
      splitData,
      effectiveFloor,
    );
    const levelToUse =
      "levelId" in floor && floor.levelId && levels[floor.levelId] ? levels[floor.levelId] : firstLevel;
    const levelFloorSettings = levelsFloorSettings[levelToUse.id];
    const joistDepthByBayId: RWTable<number> = {};

    for (const { boundaryData, assemblyFloor } of boundariesToProcess) {
      if (!boundaryData.bayId) {
        continue;
      }
      const assemblyIdToUse =
        assemblyFloor.assemblyId ?? levelFloorSettings?.floorAssembly ?? levelToUse.floorAssemblyId;
      const frameFloorAssembly = frameFloorAssemblies[assemblyIdToUse];
      const properties = extractFrameFloorAssemblyProperties(
        frameFloorAssembly,
        framingComponents,
        sheathingComponents,
      );
      if (properties?.joistProfileDescriptor !== undefined) {
        joistDepthByBayId[boundaryData.bayId] = properties.joistProfileDescriptor.width;
      }
    }

    for (const { boundaryId, boundaryData, assemblyFloor } of boundariesToProcess) {
      const fallbackLevelId = floor.levelId ?? bottomLevelId(levels);
      if (!fallbackLevelId || !levels[fallbackLevelId]) {
        issues.push({
          type: BuildingGenerationIssueType.FLOOR_GENERATION_ERROR,
          message: `Failed to find level for ${boundaryId} in floor system ${floorSystemId}`,
          handle: { type: "FLOOR", id: floorId },
        });
        continue;
      }
      const levelToUse = levels[fallbackLevelId];

      const levelFloorSettings = levelsFloorSettings[levelToUse.id];
      const assemblyIdToUse =
        assemblyFloor.assemblyId ?? levelFloorSettings?.floorAssembly ?? levelToUse.floorAssemblyId;

      const floorAssembly = frameFloorAssemblies[assemblyIdToUse] ?? slabFloorAssemblies[assemblyIdToUse];
      if (!floorAssembly) {
        issues.push({
          type: BuildingGenerationIssueType.FLOOR_GENERATION_ERROR,
          message: `Failed to find floor assembly for ${boundaryId} in floor system ${floorSystemId}`,
          handle: { type: "FLOOR", id: floorId },
        });
        continue;
      }

      const floorAssemblyEntityType = floorAssembly.entityType;

      let geometry: ReturnType<typeof processFloorBoundariesToGeometry>;
      try {
        geometry = processFloorBoundariesToGeometry(boundaryData, assemblyFloor, resolvedDatums, levels);
      } catch {
        issues.push({
          type: BuildingGenerationIssueType.FLOOR_GENERATION_ERROR,
          message: `Failed to process perimeter and openings for ${boundaryId} in floor system ${floorSystemId}`,
          handle: { type: "FLOOR", id: floorId },
        });
        continue;
      }

      if (floorAssemblyEntityType === "FRAME_FLOOR_ASSEMBLY") {
        const frameFloorAssembly = frameFloorAssemblies[assemblyIdToUse];
        const properties = extractFrameFloorAssemblyProperties(
          frameFloorAssembly,
          framingComponents,
          sheathingComponents,
        );
        if (!properties) {
          issues.push({
            type: BuildingGenerationIssueType.FLOOR_GENERATION_ERROR,
            message: `Failed to extract frame floor assembly properties for ${boundaryId} in floor system ${floorSystemId}`,
            handle: { type: "FLOOR", id: floorId },
          });
          continue;
        }
        try {
          const assembly = createFramedFloorAssembly(
            boundaryId,
            floorSystemId,
            assemblyFloor,
            properties,
            geometry,
            boundaryData,
            framingComponents,
            mirror,
            mirrorTranslationX,
            floorId,
            boundaryData.bayId,
            parentFloorSystem.openingFramingConfig,
            joistDepthByBayId,
          );
          generatedframedFloorAssemblies[boundaryId] = assembly;
        } catch {
          issues.push({
            type: BuildingGenerationIssueType.FLOOR_GENERATION_ERROR,
            message: `Failed to create framed floor assembly for ${boundaryId} in floor system ${floorSystemId}`,
            handle: { type: "FLOOR", id: floorId },
          });
          continue;
        }
      } else if (floorAssemblyEntityType === "SLAB_FLOOR_ASSEMBLY") {
        const slabFloorAssembly = slabFloorAssemblies[assemblyIdToUse];
        const properties = extractSlabFloorAssemblyProperties(slabFloorAssembly, slabComponents, sheathingComponents);
        if (!properties) {
          issues.push({
            type: BuildingGenerationIssueType.FLOOR_GENERATION_ERROR,
            message: `Failed to extract slab floor assembly properties for ${boundaryId} in floor system ${floorSystemId}`,
            handle: { type: "FLOOR", id: floorId },
          });
          continue;
        }
        if (properties.structuralThickness) {
          try {
            generatedSlabFloorAssemblies[boundaryId] = createSlabFloorAssembly(
              boundaryId,
              floorSystemId,
              properties,
              geometry,
              floorId,
              boundaryData.bayId,
            );
          } catch {
            issues.push({
              type: BuildingGenerationIssueType.FLOOR_GENERATION_ERROR,
              message: `Failed to create slab floor assembly for ${boundaryId} in floor system ${floorSystemId}`,
              handle: { type: "FLOOR", id: floorId },
            });
            continue;
          }
        }
      }
    }
  }
  return {
    framedFloorAssemblies: generatedframedFloorAssemblies,
    slabFloorAssemblies: generatedSlabFloorAssemblies,
    issues,
  };
}

export default memo(select, generateFloorHorizontalAssembliesForWorlds, compare);
